import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Briefcase, Target, TrendingUp, Swords, DollarSign, Sparkles,
  Download, Copy, Loader2, CheckCircle2, Lock,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import PublicLayout from "@/components/PublicLayout";
import LuminousAccessGate, { hasLuminousAccess } from "@/components/LuminousAccessGate";
import usePageMeta from "@/hooks/usePageMeta";

const ICONS = { Briefcase, Target, TrendingUp, Swords, DollarSign, Sparkles };

function lightMd(content) {
  // very light markdown -> JSX renderer for the brief output
  const lines = content.split("\n");
  const blocks = [];
  let para = [];
  let bullets = [];
  let tableRows = [];
  let inJsonBlock = false;

  const flushP = () => { if (para.length) { blocks.push({ t: "p", v: para.join(" ").trim() }); para = []; } };
  const flushB = () => { if (bullets.length) { blocks.push({ t: "ul", v: [...bullets] }); bullets = []; } };
  const flushT = () => { if (tableRows.length) { blocks.push({ t: "table", v: [...tableRows] }); tableRows = []; } };

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (line.startsWith("```json")) { inJsonBlock = true; flushP(); flushB(); flushT(); continue; }
    if (inJsonBlock) { if (line.startsWith("```")) inJsonBlock = false; continue; }
    if (!line.trim()) { flushP(); flushB(); flushT(); continue; }
    if (line.startsWith("**") && line.endsWith("**") && line.length < 80) {
      flushP(); flushB(); flushT();
      blocks.push({ t: "h", v: line.replace(/\*\*/g, "") });
      continue;
    }
    if (line.startsWith("- ") || line.startsWith("* ")) {
      flushP(); flushT();
      bullets.push(line.slice(2));
      continue;
    }
    if (line.startsWith("|")) {
      flushP(); flushB();
      // table row
      const cells = line.split("|").slice(1, -1).map((c) => c.trim());
      if (cells.every((c) => /^-+$/.test(c))) continue;
      tableRows.push(cells);
      continue;
    }
    flushB(); flushT();
    para.push(line);
  }
  flushP(); flushB(); flushT();

  return blocks.map((b, i) => {
    if (b.t === "h") return <h3 key={i} className="font-display font-bold text-xl mt-6 mb-2 text-[var(--ink)]">{b.v}</h3>;
    if (b.t === "p") {
      // bold inline
      const html = b.v.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
      return <p key={i} className="mt-2 text-[15px] leading-relaxed text-[var(--ink)]" dangerouslySetInnerHTML={{ __html: html }} />;
    }
    if (b.t === "ul") return (
      <ul key={i} className="mt-2 space-y-1.5">
        {b.v.map((li, j) => (
          <li key={j} className="pl-5 relative text-[15px] leading-relaxed" dangerouslySetInnerHTML={{ __html: '<span class="absolute left-0 text-[var(--oxblood)]">—</span>' + li.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>") }} />
        ))}
      </ul>
    );
    if (b.t === "table") {
      const [header, ...rows] = b.v;
      return (
        <div key={i} className="mt-4 overflow-x-auto">
          <table className="min-w-full border border-[var(--border-soft)] text-sm">
            <thead className="bg-[var(--parchment-2)]">
              <tr>{header.map((h, k) => <th key={k} className="px-3 py-2 text-left font-mono uppercase text-[10px] tracking-[0.18em] text-[var(--ink-soft)] border-b border-[var(--border-soft)]">{h}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((row, r) => (
                <tr key={r} className="border-b border-[var(--border-soft)] last:border-0">
                  {row.map((c, k) => <td key={k} className="px-3 py-2 text-[var(--ink)] align-top">{c}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    return null;
  });
}

function BriefForm({ brief, onResult, onRequireAccess }) {
  const [inputs, setInputs] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const setVal = (k, v) => setInputs((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!hasLuminousAccess()) {
      onRequireAccess?.();
      return;
    }
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post(`/briefs/generate/${brief.slug}`, { inputs });
      onResult({ ...data, brief });
    } catch (e2) { setErr(formatApiError(e2)); }
    setBusy(false);
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid={`brief-form-${brief.slug}`}>
      {(brief.inputs || []).map((inp) => (
        <div key={inp.key}>
          <label className="label-mono">{inp.label}{inp.required && " *"}</label>
          {inp.type === "select" ? (
            <select
              required={inp.required}
              value={inputs[inp.key] || inp.default || ""}
              onChange={(e) => setVal(inp.key, e.target.value)}
              className="input-editorial"
              data-testid={`brief-input-${inp.key}`}
            >
              {!inp.default && <option value="">Select…</option>}
              {inp.options.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          ) : inp.type === "textarea" ? (
            <textarea
              required={inp.required}
              rows={4}
              value={inputs[inp.key] || ""}
              onChange={(e) => setVal(inp.key, e.target.value)}
              placeholder={inp.placeholder || ""}
              className="input-editorial resize-none"
              data-testid={`brief-input-${inp.key}`}
            />
          ) : (
            <input
              type="text"
              required={inp.required}
              value={inputs[inp.key] || ""}
              onChange={(e) => setVal(inp.key, e.target.value)}
              placeholder={inp.placeholder || ""}
              className="input-editorial"
              data-testid={`brief-input-${inp.key}`}
            />
          )}
        </div>
      ))}
      <button type="submit" disabled={busy} className="btn-primary w-full justify-center" data-testid="brief-generate-button">
        {busy ? (
          <><Loader2 size={14} className="animate-spin" /> Generating…</>
        ) : hasLuminousAccess() ? (
          <>Generate Brief <Sparkles size={14} /></>
        ) : (
          <>Request Access to Generate <Lock size={14} /></>
        )}
      </button>
      {err && <p className="text-sm font-mono text-red-700">{err}</p>}
    </form>
  );
}

export default function LuminousPage() {
  usePageMeta({
    title: "Luminous AI — Executive Briefs · Luminary Insights & Advisory",
    description: "Luminous Intelligence™ — six executive brief types built on our forensic methodology. Board-ready outputs in 30–60 seconds.",
    path: "/luminous",
    keywords: "AI executive briefs, strategic intelligence AI, decision intelligence, executive brief generator",
  });

  const [briefs, setBriefs] = useState([]);
  const [active, setActive] = useState(null);
  const [result, setResult] = useState(null);
  const [gateOpen, setGateOpen] = useState(false);
  const [accessTick, setAccessTick] = useState(0); // forces re-render after grant

  useEffect(() => {
    api.get("/briefs/catalog").then((r) => {
      setBriefs(r.data || []);
      if (r.data?.length && !active) setActive(r.data[0]);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copyContent = () => {
    if (!result?.content) return;
    navigator.clipboard.writeText(result.content);
  };

  const downloadContent = () => {
    if (!result?.content) return;
    const blob = new Blob([result.content], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${result.brief.slug}-${Date.now()}.md`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <PublicLayout>
      <LuminousAccessGate
        open={gateOpen}
        onClose={() => setGateOpen(false)}
        onGrant={() => setAccessTick((t) => t + 1)}
      />
      {/* HERO */}
      <section className="terminal-bg border-b border-[var(--terminal-border)]" data-testid="luminous-page">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-14 sm:py-16">
          <div className="eyebrow" style={{ color: "var(--terminal-brass)" }}>Luminous Intelligence™</div>
          <h1 className="font-display font-black text-[clamp(2.2rem,5.6vw,4.6rem)] mt-3 leading-[0.95] tracking-tight text-[var(--terminal-text)]">
            Executive briefs.<br/>
            <span className="italic font-medium" style={{ color: "var(--signal)" }}>Generated in seconds.</span>
          </h1>
          <p className="mt-5 font-display italic text-xl text-[var(--terminal-muted)] max-w-2xl">
            Six specialist outputs trained on our forensic-grade methodology.
            Each delivered as a board-ready one-pager. Then continue the conversation with our advisory team.
          </p>
          {!hasLuminousAccess() && (
            <button
              onClick={() => setGateOpen(true)}
              className="btn-gold mt-7"
              data-testid="luminous-hero-request-access"
            >
              <Lock size={13} /> Request Access to Luminous AI
            </button>
          )}
        </div>
      </section>

      {/* BRIEF CATALOG */}
      <section className="terminal-bg border-b border-[var(--terminal-border)] py-10" data-testid="brief-catalog">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {briefs.map((b) => {
              const Icon = ICONS[b.icon] || Sparkles;
              const isActive = active?.slug === b.slug;
              return (
                <button
                  key={b.slug}
                  onClick={() => { setActive(b); setResult(null); }}
                  className={"text-left p-4 sm:p-5 border transition-all " + (isActive ? "border-[var(--signal)] bg-[var(--terminal-border)]/40" : "border-[var(--terminal-border)] hover:border-[var(--terminal-brass)] terminal-card")}
                  data-testid={`brief-tab-${b.slug}`}
                >
                  <Icon size={18} style={{ color: isActive ? "var(--signal)" : "var(--terminal-brass)" }} />
                  <div className="text-[10px] font-mono uppercase tracking-[0.18em] mt-3" style={{ color: isActive ? "var(--signal)" : "var(--terminal-brass)" }}>Brief {String(b.order).padStart(2, "0")}</div>
                  <div className="font-display font-bold text-base mt-1 leading-tight text-[var(--terminal-text)]">{b.name}</div>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* FORM + RESULT */}
      <section className="terminal-bg pb-20">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-10 grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5">
            <div className="terminal-card p-6 sm:p-7">
              {active && (
                <>
                  <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[var(--terminal-brass)]">Inputs · Brief {String(active.order).padStart(2, "0")}</div>
                  <h2 className="font-display font-bold text-3xl mt-3 text-[var(--terminal-text)] leading-tight">{active.name}</h2>
                  <p className="mt-3 text-sm text-[var(--terminal-muted)] leading-relaxed">{active.blurb}</p>
                  <div className="mt-6">
                    <BriefForm
                      key={`${active.slug}-${accessTick}`}
                      brief={active}
                      onResult={setResult}
                      onRequireAccess={() => setGateOpen(true)}
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="bg-[var(--parchment)] border border-[var(--border-soft)] p-7 sm:p-9 min-h-[520px]" data-testid="brief-result-panel">
              {!result ? (
                <div className="h-full flex flex-col items-center justify-center text-center py-20">
                  <Sparkles size={28} className="text-[var(--oxblood)]" />
                  <div className="eyebrow mt-5">Your brief will appear here</div>
                  <p className="mt-3 text-sm text-[var(--ink-soft)] max-w-md">
                    Fill the inputs on the left and hit &ldquo;Generate Brief&rdquo;. Each brief takes 20–60 seconds to assemble.
                  </p>
                </div>
              ) : (
                <div>
                  <div className="flex items-start justify-between flex-wrap gap-3">
                    <div>
                      <div className="eyebrow">{result.brief.name}</div>
                      <h2 className="font-display font-black text-3xl mt-2 text-[var(--ink)]">Brief generated.</h2>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={copyContent} className="btn-ghost" data-testid="brief-copy"><Copy size={12} /> Copy</button>
                      <button onClick={downloadContent} className="btn-ghost" data-testid="brief-download"><Download size={12} /> .md</button>
                    </div>
                  </div>
                  <div className="mt-5 flex items-center gap-3 max-w-md">
                    <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-[var(--ink-soft)]">Confidence</span>
                    <div className="confidence-bar flex-1"><span style={{ width: `${Math.round((result.confidence || 0) * 100)}%` }} /></div>
                    <span className="text-[11px] font-mono text-[var(--oxblood)]">{(result.confidence || 0).toFixed(2)}</span>
                  </div>
                  <div className="my-6 h-px bg-[var(--border-soft)]" />
                  <div className="prose-editorial max-w-none" data-testid="brief-content">
                    {lightMd(result.content || "")}
                  </div>

                  <div className="mt-8 pt-6 border-t border-[var(--border-soft)] bg-[var(--ink)] text-[var(--parchment)] p-6 -mx-7 sm:-mx-9 -mb-7 sm:-mb-9">
                    <div className="grid sm:grid-cols-2 gap-4 items-center">
                      <div>
                        <div className="eyebrow" style={{ color: "var(--brass)" }}>Go deeper</div>
                        <h3 className="font-display font-bold text-xl mt-2 leading-tight">Discuss this brief with our advisory team.</h3>
                        <p className="mt-2 text-sm text-[var(--parchment)]/75">Move from one-page brief to forensic-grade engagement.</p>
                      </div>
                      <div className="flex flex-col sm:items-end">
                        <Link to="/strategy-session" className="btn-primary w-full sm:w-auto justify-center" data-testid="brief-result-schedule">
                          Schedule a Strategy Session <ArrowRight size={14} />
                        </Link>
                        <Link to="/modules" className="mt-3 text-[11px] font-mono uppercase tracking-[0.22em] text-[var(--brass)] hover:underline">
                          See all 15 Intelligence Modules →
                        </Link>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 text-[11px] font-mono text-[var(--ink-soft)] text-center">
                    {result.quota_used}/{result.quota_limit} briefs used today{result.is_authenticated ? "" : " · Sign in for 30/day"}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* WHY DIFFERENT */}
      <section className="border-b border-[var(--ink)] bg-[var(--parchment)] py-16 sm:py-20">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6">
          <div className="eyebrow">Why this is not a chatbot</div>
          <h2 className="font-display font-black text-4xl mt-4 leading-tight">Structured outputs you can take to the next meeting.</h2>
          <div className="mt-10 grid sm:grid-cols-3 gap-6">
            {[
              { t: "Structured, not free-form", d: "Each brief follows a tested executive structure. No prompt engineering required." },
              { t: "Methodology-grounded", d: "Drafted under our 15-module forensic methodology — not generic web summarization." },
              { t: "Decision-coded", d: "Every brief ends with a Decision Implication and a path to a real strategy session." },
            ].map((p, i) => (
              <div key={i} className="bg-white border border-[var(--border-soft)] p-6">
                <CheckCircle2 size={18} className="text-[var(--viridian)]" />
                <h3 className="font-display font-bold text-xl mt-4">{p.t}</h3>
                <p className="mt-3 text-sm text-[var(--ink-soft)] leading-relaxed">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
